<template>
  <div v-if="userParameters !== null">
    <Dialog
      :visible="modelValue"
      modal
      closable
      @update:visible="$emit('update:modelValue', $event)"
      :header="$t('c.fundingForms.vericheckDialog.paymentProfile')"
      :style="{ width: '95vw', maxWidth: '24rem' }"
      :breakpoints="{ '640px': '95vw' }"
    >
      <div class="dialog-container">
        <div class="mb-1 font-bold">
          {{ $t('c.fundingForms.vericheckDialog.routingNumberLabel') }}:
        </div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          :value="routingNumber"
          inputId="routing-number"
          @input="validateRoutingNumber"
          inputmode="numeric"
          maxlength="9"
        />
        <div class="mb-1 font-bold">
          {{ $t('c.fundingForms.vericheckDialog.accountNumberLabel') }}:
        </div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          :value="accountNumber"
          inputId="account-number"
          @input="validateAccountNumber"
          inputmode="numeric"
          maxlength="20"
        />
        <div style="margin: 10px 0px 20px 0px">
          <div class="mb-1 font-bold">
            {{ $t('c.fundingForms.vericheckDialog.accountTypeLabel') }}:
          </div>
          <div
            class="p-field-radiobutton"
            v-for="account in accountTypes"
            :key="account.value"
            style="margin-bottom: 10px"
          >
            <RadioButton
              v-model="selectedAccountType"
              :inputId="account.value"
              name="accountTypes"
              :value="account.value"
              class="mr-1"
            />
            <label :for="account.value">{{ account.label }}</label>
          </div>
        </div>
        <div class="mb-1 font-bold">{{ $t('c.fundingForms.vericheckDialog.firstNameLabel') }}:</div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          v-model="userParameters.firstName"
          inputId="first-name"
          type="text"
          maxlength="25"
        />
        <div class="mb-1 font-bold">{{ $t('c.fundingForms.vericheckDialog.lastNameLabel') }}:</div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          v-model="userParameters.lastName"
          inputId="last-name"
          type="text"
          maxlength="25"
        />
        <div class="mb-1 font-bold">{{ $t('c.fundingForms.vericheckDialog.addressLabel') }}:</div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          v-model="userParameters.address1"
          inputId="address"
          type="text"
        />
        <div class="mb-1 font-bold">{{ $t('c.fundingForms.vericheckDialog.cityLabel') }}:</div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          v-model="userParameters.city"
          inputId="city"
          type="text"
        />
        <div class="mb-1 font-bold">{{ $t('c.fundingForms.vericheckDialog.stateLabel') }}:</div>
        <Select
          v-model="userParameters.state"
          :options="states"
          optionLabel="label"
          optionValue="value"
          :placeholder="$t('c.fundingForms.vericheckDialog.selectAState')"
          filter
          class="mb-2 w-full md:w-16rem"
        />
        <div class="mb-1 font-bold">{{ $t('c.fundingForms.transitDialog.zipCodeLabel') }}:</div>
        <InputText
          class="mb-2 w-full md:w-16rem"
          v-model="userParameters.zipCode"
          inputId="zip-code"
          style="width: 265px"
          maxlength="5"
          inputmode="numeric"
        />
      </div>
      <div style="margin: 25px 0 25px 25px">
        <Button
          :label="$t('c.fundingForms.vericheckDialog.saveLabel')"
          @click="save()"
          class="save-button w-8rem"
          :loading="saving"
          raised
        />
        <Button
          :label="$t('c.fundingForms.vericheckDialog.cancelLabel')"
          @click="$emit('update:modelValue', false)"
          text
          raised
          class="ml-5 w-8rem"
        />
      </div>
    </Dialog>
  </div>
</template>

<script lang="ts">
import { API } from '@/modules/api'
import type { UserData } from '@/modules/api/get/userData'
import { sealAchPayload } from '@/modules/crypto/achRelaySeal'

export interface AccountTypeOption {
  label: string
  value: string
}

export default {
  name: 'TsysAchDialog',
  components: {},

  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    districtId: {
      type: Number,
      required: true
    },
    initialUserData: {
      type: [Object, null] as unknown as () => UserData | null,
      default: null
    }
  },

  computed: {},

  data() {
    return {
      userParameters: null as UserData | null,
      routingNumber: null as string | null,
      accountNumber: null as string | null,
      accountTypes: [] as AccountTypeOption[],
      selectedAccountType: 'checking',
      saving: false,
      states: [
        { label: 'Alabama', value: 'AL' },
        { label: 'Alaska', value: 'AK' },
        { label: 'Arizona', value: 'AZ' },
        { label: 'Arkansas', value: 'AR' },
        { label: 'California', value: 'CA' },
        { label: 'Colorado', value: 'CO' },
        { label: 'Connecticut', value: 'CT' },
        { label: 'Delaware', value: 'DE' },
        { label: 'District of Columbia', value: 'DC' },
        { label: 'Florida', value: 'FL' },
        { label: 'Georgia', value: 'GA' },
        { label: 'Hawaii', value: 'HI' },
        { label: 'Idaho', value: 'ID' },
        { label: 'Illinois', value: 'IL' },
        { label: 'Indiana', value: 'IN' },
        { label: 'Iowa', value: 'IA' },
        { label: 'Kansas', value: 'KS' },
        { label: 'Kentucky', value: 'KY' },
        { label: 'Louisiana', value: 'LA' },
        { label: 'Maine', value: 'ME' },
        { label: 'Maryland', value: 'MD' },
        { label: 'Massachusetts', value: 'MA' },
        { label: 'Michigan', value: 'MI' },
        { label: 'Minnesota', value: 'MN' },
        { label: 'Mississippi', value: 'MS' },
        { label: 'Missouri', value: 'MO' },
        { label: 'Montana', value: 'MT' },
        { label: 'Nebraska', value: 'NE' },
        { label: 'Nevada', value: 'NV' },
        { label: 'New Hampshire', value: 'NH' },
        { label: 'New Jersey', value: 'NJ' },
        { label: 'New Mexico', value: 'NM' },
        { label: 'New York', value: 'NY' },
        { label: 'North Carolina', value: 'NC' },
        { label: 'North Dakota', value: 'ND' },
        { label: 'Ohio', value: 'OH' },
        { label: 'Oklahoma', value: 'OK' },
        { label: 'Oregon', value: 'OR' },
        { label: 'Pennsylvania', value: 'PA' },
        { label: 'Rhode Island', value: 'RI' },
        { label: 'South Carolina', value: 'SC' },
        { label: 'South Dakota', value: 'SD' },
        { label: 'Tennessee', value: 'TN' },
        { label: 'Texas', value: 'TX' },
        { label: 'Utah', value: 'UT' },
        { label: 'Vermont', value: 'VT' },
        { label: 'Virginia', value: 'VA' },
        { label: 'Washington', value: 'WA' },
        { label: 'West Virginia', value: 'WV' },
        { label: 'Wisconsin', value: 'WI' },
        { label: 'Wyoming', value: 'WY' }
      ]
    }
  },

  methods: {
    validateRoutingNumber(event: any) {
      const sanitized = event.target.value.replace(/\D/g, '').slice(0, 9)
      event.target.value = sanitized
      this.routingNumber = sanitized
    },
    validateAccountNumber(event: any) {
      // The payment API accepts ^[A-Za-z0-9]{4,20}$, so LETTERS MUST SURVIVE.
      // Several institutions issue alphanumeric account numbers, and stripping
      // the letters turned one into a different, still-valid-looking number that
      // was vaulted and later debited — the same silent-wrong-account failure the
      // old slice(0, 17) caused, by another route.
      //
      // Nothing is truncated here either. A slice only moves the silent-
      // truncation threshold instead of removing it: a pasted 22-character
      // account became a 20-character one that still passed server validation.
      // Length is checked in hasErrors(), where being wrong is VISIBLE.
      const sanitized = event.target.value.replace(/[^A-Za-z0-9]/g, '')
      event.target.value = sanitized
      this.accountNumber = sanitized
    },
    async save() {
      if (this.hasErrors()) {
        return
      }
      this.saving = true
      try {
        // 1. RSA-OAEP-seal the bank data to the payment API's PINNED public key
        //    (encrypt-through-core). The plaintext never leaves the browser; the
        //    sealed blob is opaque to core.
        //
        //    Sealing runs FIRST deliberately. It is the step most likely to fail
        //    (a missing or malformed VITE_ACH_RELAY_PUBKEY), and minting first
        //    left an un-prunable 'pending' row in the payment API's session table
        //    on every failed attempt while consuming a slot of a throttle bucket
        //    that is shared platform-wide — so one bad deploy's retry loop could
        //    429 ACH capture for healthy districts.
        const encPayload = await sealAchPayload({
          routing_number: this.routingNumber as string,
          account_number: this.accountNumber as string,
          account_type: this.selectedAccountType === 'saving' ? 'SAVING' : 'CHECKING'
        })

        // 2. Ask core to mint a single-use tokenization session (core binds it
        //    to the authenticated family on its own side).
        const session = await API.Post.achSession(this.districtId)

        // 3. Hand core the session_key + sealed blob (+ billing). Core relays the
        //    opaque blob server-to-server to the payment API and gets back the
        //    AUTHORITATIVE token — the browser never touches the payment API.
        await API.Post.addTsysAch({
          district_id: this.districtId,
          session_key: session.session_key,
          enc_payload: encPayload,
          billing_address: {
            given_name: this.userParameters?.firstName,
            surname: this.userParameters?.lastName,
            address_1: this.userParameters?.address1,
            city: this.userParameters?.city,
            region: this.userParameters?.state,
            postal_code:
              this.userParameters?.zipCode != null ? String(this.userParameters.zipCode) : '',
            country: 'US'
          }
        })

        this.$toast.add({
          severity: 'success',
          summary: this.$t('c.fundingForms.vericheckDialog.success'),
          detail: this.$t('c.fundingForms.vericheckDialog.savedSuccessfully'),
          life: 6000
        })
        // No resetBankFields() here — the modelValue watcher clears the fields on
        // every close, so this path is covered along with cancel, the X, and a
        // failed save.
        this.$emit('update:modelValue', false)
        this.$emit('funding-info-saved')
      } catch (err) {
        console.error(err)
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.vericheckDialog.error'),
          detail: this.$t('c.fundingForms.vericheckDialog.saveFailed'),
          life: 6000
        })
      } finally {
        this.saving = false
      }
    },
    resetBankFields() {
      // The dialog is driven by :visible and is never destroyed on close, so
      // without this the previous account's digits stay in component state — a
      // retention problem, and a wrong prefill for a family adding a second bank
      // account. Called from the modelValue watcher so it covers every way out of
      // the dialog: save, cancel, the closable X, and a failed save.
      this.routingNumber = null
      this.accountNumber = null
      this.selectedAccountType = 'checking'
    },
    getUserData() {
      if (this.initialUserData) {
        this.userParameters = { ...this.initialUserData }
        return
      }
      API.Get.userData()
        .then(results => {
          this.userParameters = results
        })
        .catch(err => {
          // The whole dialog sits behind v-if="userParameters !== null", so a
          // failed lookup used to mean clicking "Bank Account" did nothing at
          // all — no dialog, no toast, no spinner, every single time. Tell the
          // family something went wrong and close the dialog, so the click has a
          // visible result instead of dead air.
          console.error(err)
          this.$toast.add({
            severity: 'error',
            summary: this.$t('c.fundingForms.vericheckDialog.error'),
            detail: this.$t('c.fundingForms.vericheckDialog.userDataFailed'),
            life: 6000
          })
          this.$emit('update:modelValue', false)
        })
    },
    hasErrors() {
      let hasError = false
      if (!this.accountNumber) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.vericheckDialog.error'),
          detail: this.$t('c.fundingForms.vericheckDialog.accountNumberRequired'),
          life: 6000
        })
        hasError = true
      } else if (!/^[A-Za-z0-9]{4,20}$/.test(this.accountNumber)) {
        // Same reasoning as the routing-number check below. Without this a 2-
        // character account was sealed, burned a single-use tokenization session,
        // and came back as the generic "Failed to save bank account" with no clue
        // which field was wrong. Rejecting an over-length value here is also what
        // lets validateAccountNumber stop truncating silently.
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.vericheckDialog.error'),
          detail: this.$t('c.fundingForms.vericheckDialog.accountNumberInvalid'),
          life: 6000
        })
        hasError = true
      }
      if (!this.routingNumber) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.vericheckDialog.error'),
          detail: this.$t('c.fundingForms.vericheckDialog.routingNumberRequired'),
          life: 6000
        })
        hasError = true
      } else if (!/^\d{9}$/.test(this.routingNumber)) {
        // An ABA routing number is exactly 9 digits. Without this the bad value
        // is sealed, relayed through core to the payment API, and comes back as a
        // generic "save failed" toast having burned a tokenization session.
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.vericheckDialog.error'),
          detail: this.$t('c.fundingForms.vericheckDialog.routingNumberInvalid'),
          life: 6000
        })
        hasError = true
      }
      if (!this.userParameters?.zipCode) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.vericheckDialog.error'),
          detail: this.$t('c.fundingForms.vericheckDialog.zipCodeRequired'),
          life: 6000
        })
        hasError = true
      } else if (!this.userParameters?.zipCode?.toString().match(/^\d{5}$/)) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('c.fundingForms.vericheckDialog.error'),
          detail: this.$t('c.fundingForms.vericheckDialog.zipCodeInvalid'),
          life: 6000
        })
        hasError = true
      }
      return hasError
    }
  },

  mounted() {
    this.accountTypes = [
      { label: this.$t('c.fundingForms.vericheckDialog.checkingAccountLabel'), value: 'checking' },
      { label: this.$t('c.fundingForms.vericheckDialog.savingsAccountLabel'), value: 'saving' }
    ]
  },

  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.getUserData()
      } else {
        // Reset on EVERY exit, not just a successful save. The dialog is driven
        // by :visible and is never destroyed, and cancel, the closable X, and a
        // failed save all used to leave the routing and account numbers in
        // component state — which the :value bindings then re-displayed,
        // pre-filled, the next time it opened. Closing is the one event all four
        // exits share, so the reset belongs here.
        this.resetBankFields()
      }
    }
  }
}
</script>

<style>
.dialog-container {
  padding-left: 1.25rem;
}
</style>
